import { useRef, useState } from 'react';
import {
  connectBank,
  createFinanceAccount,
  importOrdersCsv,
  importTransactionsCsv,
  syncBank,
  type BankStatus,
  type FinanceAccount,
} from '../api.js';
import { Button, Card, Icon } from '../ui/index.js';

/** Pieces shared by more than one Money tab. */

// --- Date ranges ---------------------------------------------------------------

export type RangeKey = 'month' | '30d' | '90d' | '1y';
export const RANGES: readonly { value: RangeKey; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
];
const RANGE_DAYS: Record<Exclude<RangeKey, 'month'>, number> = { '30d': 30, '90d': 90, '1y': 365 };

export function rangeOf(key: RangeKey, now = new Date()): { from: string; to: string } {
  const from =
    key === 'month' ? new Date(now.getFullYear(), now.getMonth(), 1) : new Date(now.getTime() - RANGE_DAYS[key] * 86_400_000);
  return { from: from.toISOString(), to: now.toISOString() };
}

// --- Money input ---------------------------------------------------------------

/** "$12,500.5" → 1250050. Parsed as a string so no float ever touches the cents; null when unreadable. */
export function parseCents(s: string): number | null {
  const m = /^\s*(-)?\s*\$?\s*([\d,]*)(?:\.(\d{0,2})\d*)?\s*$/.exec(s);
  if (!m || (!m[2] && !m[3])) return null;
  const whole = Number((m[2] ?? '').replace(/,/g, '') || '0');
  const frac = Number((m[3] ?? '').padEnd(2, '0'));
  const cents = whole * 100 + frac;
  return m[1] ? -cents : cents;
}

// --- Small UI bits -------------------------------------------------------------

export function SkeletonGroup(): JSX.Element {
  return (
    <ul className="ui-group" aria-busy="true" aria-label="Loading">
      <li className="hb-skelrow" />
      <li className="hb-skelrow" />
      <li className="hb-skelrow" />
    </ul>
  );
}

export function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }): JSX.Element {
  return (
    <label className="mn-search">
      <Icon name="search" size={18} />
      <input type="search" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} />
    </label>
  );
}

/** "just now", "3h ago", "2d ago". */
export function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

export function BankFooter({ bank, onChange }: { bank: BankStatus; onChange: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sync = async () => {
    setBusy(true);
    setError(null);
    try {
      await syncBank();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="mn-bank">
      {bank.lastSyncAt ? `Synced ${ago(bank.lastSyncAt)}` : 'Not synced yet'} ·{' '}
      <button type="button" className="mn-bank__sync" onClick={() => void sync()} disabled={busy}>
        {busy ? 'Syncing…' : 'Sync now'}
      </button>
      {(error ?? bank.lastError) && <span className="mn-bank__err">{error ?? bank.lastError}</span>}
    </span>
  );
}

// --- Accounts ------------------------------------------------------------------

export const ACCOUNT_TYPES: readonly [string, string][] = [
  ['checking', 'Checking'],
  ['savings', 'Savings'],
  ['credit', 'Credit card'],
  ['investment', 'Investment'],
  ['loan', 'Loan'],
  ['cash', 'Cash'],
  ['vehicle', 'Vehicle'],
  ['property', 'Property'],
];

export function AccountForm({ onDone, cta = 'Add account' }: { onDone: () => void; cta?: string }): JSX.Element {
  const [name, setName] = useState('');
  const [type, setType] = useState('checking');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createFinanceAccount({ name: name.trim(), type });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hb-form" onSubmit={submit}>
      <label className="hb-field">
        <span>Account name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Everyday Checking" required />
      </label>
      <label className="hb-field">
        <span>Type</span>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {ACCOUNT_TYPES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" size="lg" block disabled={busy || !name.trim()}>
        {busy ? 'Adding…' : cta}
      </Button>
    </form>
  );
}

/**
 * Link every bank through one SimpleFIN Bridge account: the owner connects
 * their banks on SimpleFIN's site, then pastes the one-time setup token here.
 * The server claims it, keeps the access URL to itself, and runs the first sync.
 */
export function ConnectBank({ onDone }: { onDone: () => void }): JSX.Element {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const res = await connectBank(token.trim());
      if (res.sync) {
        setToken('');
        onDone();
      } else {
        setMessage(`Connected — the first sync failed (${res.error ?? 'unknown error'}). It retries every 4 hours.`);
        onDone();
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not connect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card eyebrow="Bank feed" title="Connect your banks" className="mn-first">
      <ol className="hb-hint mn-first__steps">
        <li>
          Sign in at{' '}
          <a href="https://beta-bridge.simplefin.org" target="_blank" rel="noreferrer">
            SimpleFIN Bridge
          </a>{' '}
          and connect each bank and card.
        </li>
        <li>Choose <b>New app connection</b> and copy the setup token.</li>
        <li>Paste it below. History fills in and syncs every 4 hours from then on.</li>
      </ol>
      <form className="hb-form" onSubmit={submit}>
        <label className="hb-field">
          <span>Setup token</span>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="aHR0cHM6Ly9iZXRhLWJyaWRnZS5zaW1wbGVmaW4ub3Jn…"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </label>
        {message && <p className="hb-hint" role="alert">{message}</p>}
        <Button type="submit" size="lg" block disabled={busy || !token.trim()}>
          {busy ? 'Connecting… (first sync can take a minute)' : 'Connect'}
        </Button>
      </form>
      <p className="hb-hint mn-first__hint">Read-only: SimpleFIN has no way to move money.</p>
    </Card>
  );
}

// --- Import --------------------------------------------------------------------

export function ImportPanel({ accounts, onDone }: { accounts: FinanceAccount[]; onDone: () => void }): JSX.Element {
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const [orderSource, setOrderSource] = useState('amazon');
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const txnInput = useRef<HTMLInputElement>(null);
  const orderInput = useRef<HTMLInputElement>(null);

  /** Read the picked file as text and hand it to the server to parse. */
  const upload = async (
    file: File | undefined,
    run: (csv: string) => Promise<{ imported: number; parsed: number; skipped: Array<{ line: number; reason: string }> }>,
  ) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const csv = await file.text();
      const res = await run(csv);
      const skipNote = res.skipped.length
        ? ` ${res.skipped.length} row(s) skipped (first: line ${res.skipped[0]!.line}, ${res.skipped[0]!.reason}).`
        : '';
      setResult(`Imported ${res.imported} of ${res.parsed} rows.${skipNote}`);
      onDone();
    } catch (err) {
      setResult(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hb-form mn-import">
      {result && (
        <p className="mn-import__result" role="status">
          <Icon name="check" size={18} /> {result}
        </p>
      )}

      <section className="mn-import__part">
        <h3 className="mn-import__title">Bank statement</h3>
        <p className="hb-hint">
          Columns are matched by name, so most exports work as-is — a single signed Amount column or separate
          Debit/Credit both. Re-importing the same file updates rather than duplicates.
        </p>
        <label className="hb-field">
          <span>Account</span>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={txnInput}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => void upload(e.target.files?.[0], (csv) => importTransactionsCsv(accountId, csv))}
        />
        <Button block disabled={busy || !accountId} onClick={() => txnInput.current?.click()}>
          Choose CSV
        </Button>
      </section>

      <section className="mn-import__part">
        <h3 className="mn-import__title">Amazon / eBay orders</h3>
        <p className="hb-hint">What was actually bought, not just the amount the bank saw. Both sites export order history as CSV.</p>
        <label className="hb-field">
          <span>Source</span>
          <select value={orderSource} onChange={(e) => setOrderSource(e.target.value)}>
            <option value="amazon">Amazon</option>
            <option value="ebay">eBay</option>
          </select>
        </label>
        <input
          ref={orderInput}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => void upload(e.target.files?.[0], (csv) => importOrdersCsv(orderSource, csv))}
        />
        <Button variant="secondary" block disabled={busy} onClick={() => orderInput.current?.click()}>
          Choose CSV
        </Button>
      </section>
    </div>
  );
}
